import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Icon, Image, Label, Menu } from "semantic-ui-react";
import { useAuth, homePathFor } from "../context/AuthContext";
import { useBooking } from "../context/BookingContext";
import NotificationBell from "./NotificationBell";
import logo from "../resources/logo.png";

function Header() {
  const { currentUser, logout } = useAuth();
  const { guestHouses } = useBooking();
  const location = useLocation();
  const navigate = useNavigate();
  const headerRef = useRef(null);

  // Header is position: fixed (see below), so it's out of document flow —
  // page content needs top padding equal to its real height to avoid being
  // hidden underneath it. Measure and expose it as a CSS var since Semantic
  // UI's Menu height isn't a fixed constant (it wraps on narrow screens).
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const setHeight = () =>
      document.documentElement.style.setProperty(
        "--header-height",
        `${el.offsetHeight}px`,
      );
    setHeight();
    const observer = new ResizeObserver(setHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [currentUser]);

  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      navigate("/login");
    }
  };

  const isActive = (path) => location.pathname === path;

  if (!currentUser) return null;

  const isAdmin = currentUser.role === "admin";
  const isNight = currentUser.role === "night";
  const isSuperAdmin = currentUser.apiRole === "SUPER_ADMIN";
  /* For a GH_ADMIN, ghApi.list() (behind useBooking()) is already scoped
     server-side to only their assigned guest house(s) — Super Admin sees all,
     so it isn't meaningful to show a guest house name for them. */
  const assignedGhNames =
    isAdmin && !isSuperAdmin
      ? (guestHouses || [])
          .map((g) => g.name)
          .filter(Boolean)
          .join(", ")
      : "";
  const roleLabel = isNight
    ? "Night Staff"
    : !isAdmin
      ? "Guest"
      : isSuperAdmin
        ? "Super Administrator"
        : "Administrator";

  return (
    <header
      ref={headerRef}
      style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100 }}
    >
      <Menu borderless className="bhu-navbar">
        {/* Brand */}
        <Menu.Item
          as={Link}
          to={homePathFor(currentUser.role)}
          header
          style={{ marginLeft: 12 }}
          onClick={() => {
            /* Admin dashboard is a single route with internal tabs — navigating
               to "/gh-admin" while already there is a no-op for the router, so
               tell it directly to reset back to the Summary tab. */
            if (isAdmin) window.dispatchEvent(new Event("bhu:admin-go-home"));
          }}
        >
          <Image
            src={logo}
            alt="BHU Logo"
            style={{ height: 28, width: "auto", margin: "0 10px 0 0" }}
          />
          <h3
            style={{
              marginLeft: 8,
              marginTop: 0,
              marginBottom: 0,
              fontSize: "1.05rem",
            }}
          >
            Atithi
          </h3>
        </Menu.Item>

        {/* Navigation. Admins navigate by the dashboard's own tabs; night staff
            have nowhere to go — their session is the one screen — so both get
            the bar for its identity and Logout only. */}
        {!isAdmin && !isNight && (
          <>
            <Menu.Item
              as={Link}
              to="/my-bookings"
              active={isActive("/my-bookings")}
            >
              <Icon name="calendar check outline" />
              My Bookings
            </Menu.Item>
            <Menu.Item as={Link} to="/rooms" active={isActive("/rooms")}>
              Book Now
            </Menu.Item>
          </>
        )}

        {/* Right side */}
        <Menu.Menu position="right">
          {isAdmin && (
            <Menu.Item>
              <NotificationBell />
            </Menu.Item>
          )}
          <Menu.Item>
            <Label
              circular
              color="blue"
              style={{ marginRight: 10, fontSize: "0.78rem" }}
            >
              {currentUser.fullName.charAt(0).toUpperCase()}
            </Label>
            {/* Tight line-height throughout: for a GH_ADMIN this block is
                three lines, and at the inherited 1.6 it — not the logo — is
                what sets the navbar's height. */}
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontSize: "0.8rem", fontWeight: 600 }}>
                {currentUser.fullName}
              </div>
              <div style={{ fontSize: "0.65rem", opacity: 0.6 }}>
                {roleLabel}
              </div>
              {assignedGhNames && (
                <div style={{ fontSize: "0.6rem", opacity: 0.6 }}>
                  {assignedGhNames}
                </div>
              )}
            </div>
          </Menu.Item>
          <Menu.Item
            onClick={loggingOut ? undefined : handleLogout}
            disabled={loggingOut}
            title="Logout"
          >
            <Icon name={loggingOut ? "spinner" : "sign out"} loading={loggingOut} />
            {loggingOut ? "Logging out…" : "Logout"}
          </Menu.Item>
        </Menu.Menu>
      </Menu>
    </header>
  );
}

export default Header;
